import { CalendarioMensual, EmptyState, type TonoCalendario } from "@/components/ds";
import type { DestinatariosOpciones, Programa, ProgramaTematica } from "@/types/capacitaciones-ia";

/**
 * Interruptor único de la vista "Plan de envíos" (el calendario mensual).
 *
 * Está en `false` porque el calendario confunde: convive con la lista de
 * programas mostrando los mismos envíos con otra forma, y quien entra no
 * distingue si está mirando lo que va a salir o lo que ya salió. La feature
 * está completa y el backend no cambió — se OCULTA, no se borra (mismo criterio
 * que `ENVIO_MANUAL_HABILITADO` en `ProgramarEnviosDrawer.tsx`).
 *
 * Re-prenderlo = este booleano en `true`. Gobierna: el toggle Lista / Plan de
 * envíos de `ProgramaCapacitaciones`, el `?vista=plan` de la URL, el salto
 * automático al calendario tras programar, y el enlace "Ver el plan de lo que
 * falta" de `CapacitacionesEnviadas`.
 *
 * Tipado `: boolean` a propósito: con el literal `false`, TS da por imposibles
 * las comparaciones contra `"plan"` que los archivos conservan para cuando se
 * vuelva a prender.
 */
export const PLAN_DE_ENVIOS_HABILITADO: boolean = false;

interface BloqueEnvio {
  tematicaId: number;
  titulo: string;
  fecha: Date;
  tono: TonoCalendario;
  subtitulo: string;
}

function parseFechaLocal(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

function resumenDestinatario(tema: ProgramaTematica, opciones: DestinatariosOpciones | null): string {
  const tipo = tema.destinatario_tipo ?? "empresa";
  const refs = tema.destinatario_refs ?? [];
  if (tipo === "empresa") return `Toda la empresa (${opciones?.dotacion ?? "—"})`;
  if (tipo === "cargo") {
    const seleccion = (opciones?.cargos ?? []).filter((c) => refs.includes(c.id));
    const n = seleccion.reduce((acc, c) => acc + c.conteo, 0);
    return `${seleccion.map((c) => c.nombre).join(", ") || "Por cargo"} (${n})`;
  }
  if (tipo === "area") {
    const seleccion = (opciones?.areas ?? []).filter((a) => refs.includes(a.id));
    const n = seleccion.reduce((acc, a) => acc + a.conteo, 0);
    return `${seleccion.map((a) => a.nombre).join(", ") || "Por área"} (${n})`;
  }
  return `Trabajador específico (${refs.length})`;
}

function bloquesDe(programas: Programa[], opciones: DestinatariosOpciones | null): BloqueEnvio[] {
  const bloques: BloqueEnvio[] = [];
  for (const programa of programas) {
    if (programa.estado === "archivado") continue;
    for (const tema of programa.temas) {
      if (tema.estado_negocio === "pendiente" || !tema.fecha_programada) continue;
      const tono: TonoCalendario =
        tema.estado_negocio === "enviada" ? "enviada" : tema.modo_envio === "manual" ? "manual" : "programada";
      bloques.push({
        tematicaId: tema.id,
        titulo: tema.titulo,
        fecha: parseFechaLocal(tema.fecha_programada),
        tono,
        subtitulo: resumenDestinatario(tema, opciones),
      });
    }
  }
  return bloques;
}

export interface PlanDeEnviosProps {
  programas: Programa[];
  opciones: DestinatariosOpciones | null;
  mes: Date;
  onCambiarMes: (mes: Date) => void;
}

/**
 * Vista "Plan de envíos" (E4) — calendario mensual construido desde
 * `programas[].tematicas`, NUNCA desde datos aparte (spec §3.2, design #1399
 * decisión 3: la cadencia se MATERIALIZA en `fecha_programada` al programar;
 * este calendario sólo lee esa columna, no la reinterpreta).
 */
export function PlanDeEnvios({ programas, opciones, mes, onCambiarMes }: PlanDeEnviosProps) {
  const bloques = bloquesDe(programas, opciones);

  return (
    <CalendarioMensual<BloqueEnvio>
      mes={mes}
      onCambiarMes={onCambiarMes}
      bloques={bloques}
      getFecha={(b) => b.fecha}
      tono={(b) => b.tono}
      leyenda="Cada bloque es una temática que va a salir. Todavía no existe cuestionario."
      renderBloque={(b) => (
        <>
          <span className="fc-truncate text-[10.5px] font-medium">{b.titulo}</span>
          <span className="fc-truncate text-soft text-[9.5px]">{b.subtitulo}</span>
        </>
      )}
      vacio={
        <EmptyState
          icono="calendar_month"
          mensaje="Todavía no hay envíos programados. Programa uno desde la lista de programas."
          className="py-16"
        />
      }
    />
  );
}
